import { 
	createSlice,
	createSelector,
	createAsyncThunk,
	createEntityAdapter
} from '@reduxjs/toolkit'
import {envAwarePost} from '../../util/util'

export const saveClass = createAsyncThunk(
	'saveNewLiveClass',
	async payload => await envAwarePost('api/classes/api/',{
		action:'saveNewLiveClass',
		...payload
	})
)

export const updateLiveClass = createAsyncThunk(
	'updateLiveClass',
	async payload => await envAwarePost('api/classes/api/',{
		action:'updateLiveClass',
		...payload
	})
)

export const deleteLiveClass = createAsyncThunk(
	'deleteLiveClass',
	async payload => await envAwarePost('api/classes/api/',{
		action:'deleteLiveClass',
		...payload
	})
)

export const getUpcomingClasses = createAsyncThunk(
	'getUpcomingClasses',
	async () => await envAwarePost('api/classes/api/',{action:'allClasses'})
)

export const getPreviousClasses = createAsyncThunk(
	'getPreviousClasses',
	async () => await envAwarePost('api/classes/api/',{action:'getAllPreviousClasses'})
)

export const getClasses = createAsyncThunk(
	'getClasses',
	async (_,{getState}) => {
		const state = getState()
		//get current classes
		const current = await envAwarePost('api/classes/api/',{action:'allClasses'})
		//clients only need current classes
		//trainers need current and past
		if(state.users.current_user.account_type === 'client'){
			return current
		}else{
			//get classes from the past
			const past = await envAwarePost('api/classes/api/',{action:'getAllPreviousClasses'})
			//mark these classes as being in the past
			const pastWithFlag = past.data.map(c=>{c.isPast = true})
			//merge past and current and return 
			const merged = [...current.data,...past.data]
			//return joined response
			return {
				success:current.success === '1' && past.success === '1' ? '1' : '0',
				message:`${current.message} ${past.message}`,
				data:merged
			}
		}
		
	}
)

export const registerForFree = createAsyncThunk(
	'registerForClass',
	async (payload,{dispatch}) => await envAwarePost('api/classes/api/',{
		action:'registerForClass',
		...payload
	})
)

const adapter = createEntityAdapter({
	selectId: ent => ent._id
})

const slice = createSlice({
	name: 'liveClasses',
	initialState: adapter.getInitialState({
		status : 'pending',
		upcomingStatus : 'pending',
		previousStatus : 'pending',
		editor : { show:false, id:null },
		participants : { show:false, id:null },
		messenger : { show:false, id: null},
	}),
	reducers : {
		updateEditor : (state,action)=>{
			state.editor = {
				...state.editor,
				...action.payload
			}
		},
		updateMessenger : (state,action)=>{
			state.messenger = {
				...state.messenger,
				...action.payload
			}
		},
		updateParticipants : (state,action)=>{
			state.participants = {
				...state.participants,
				...action.payload
			}
		}
	},
	extraReducers : {
		[getClasses.fulfilled] : (state,action) => {
			state.status = 'idle'
			const {message,success,data} = action.payload
			if( success === '1' ){
				adapter.upsertMany(state,data)
			} else { 
				alert(message)
			}
		},
		[getUpcomingClasses.fulfilled] : (state,action) => {
			state.upcomingStatus = 'idle'
			const {message,success,data} = action.payload
			if( success === '1' ){
				adapter.upsertMany(state,data)
			} else { 
				alert(message)
			}
		},
		[getPreviousClasses.fulfilled] : (state,action) => {
			state.previousStatus = 'idle'
			const {message,success,data} = action.payload
			if( success === '1' ){
				//mark these classes as being in the past
				const pastWithFlag = data.map(c=>{
					c.isPast = true
					return c
				})
				adapter.upsertMany(state,pastWithFlag)
			} else { 
				alert(message)
			}
		},
		[deleteLiveClass.pending] : (state,action) => {
			const {class_id} = action.meta.arg
			adapter.removeOne(state,class_id)
		}
	}
})

export const { 
	updateEditor,
	updateMessenger,
	updateParticipants
} = slice.actions

export const selectShowEditor = state => state.liveClasses.editor.show
export const selectShowMessenger = state => state.liveClasses.messenger.show
export const selectMessengerClass = state => selectClassById(state,state.liveClasses.messenger.id)
export const selectShowParticipants = state => state.liveClasses.participants.show
export const selectParticipantsClassId = state => state.liveClasses.participants.id

export const selectClassToEdit = state => state.liveClasses.entities[state.liveClasses.editor.id]

// Export the customized selectors for this adapter using `getSelectors`
export const {
	selectAll: selectAllClasses,
	selectById: selectClassById,
	selectIds: selectClassIds
} = adapter.getSelectors( state => state.liveClasses )

export const selectUpcomingClasses = createSelector(
	selectAllClasses,
	allClasses => allClasses.filter(c=>!c.isPast)
)
export const selectPastClasses = createSelector(
	selectAllClasses,
	allClasses => allClasses.filter(c=>c.isPast)
)

export default slice.reducer